import { resolveTourStepsForRole } from "../resolveTourStepsForRole";

const LEADERSHIP = ["Admin", "Sales Director", "Manager"];
const SETTINGS_FULL = ["Admin", "Sales Director"];
const ADMIN_ONLY = ["Admin"];

function tour(steps, role, options) {
  return resolveTourStepsForRole(steps, role, options);
}

function copy(title, content) {
  return {
    Admin: { title, content },
    Manager: { title, content },
    "Sales Rep": { title, content },
  };
}

function welcome(title, content) {
  return {
    selector: "body",
    placement: "center",
    copy: copy(title, content),
  };
}

export const SALES_PIPELINE_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Sales Pipeline",
        "Track proposals sent, pending deals, and closed won for the month. Use this page for funnel health and team leaderboard."
      ),
      {
        selector: '[data-tour="sales-pipeline-kpis"]',
        placement: "bottom",
        copy: copy(
          "Pipeline metrics",
          "These cards show proposals sent this month, deals still awaiting the customer, and closed won counts for the selected period."
        ),
      },
      {
        selector: '[data-tour="sales-pipeline-month-nav"]',
        placement: "bottom",
        copy: copy(
          "Month navigation",
          "Move between months to compare pipeline performance. Territory filters (when available) narrow metrics to one market."
        ),
      },
      {
        selector: '[data-tour="sales-pipeline-performance"]',
        placement: "top",
        copy: copy(
          "Performance & activity",
          "Review proposal outcomes, team leaderboard, and recent activity. Click activity rows to open related leads when available."
        ),
      },
    ],
    role,
    options
  );

export const MESSAGE_CENTER_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Message Center",
        "Deprecated. This inbox is no longer in the sidebar. The page stays until nothing else opens it."
      ),
      {
        selector: '[data-tour="message-center-create-new"]',
        placement: "left",
        copy: copy(
          "Create new",
          "Start a notification, follow-up, or task for yourself or your team without leaving this page."
        ),
      },
      {
        selector: '[data-tour="message-center-filters"]',
        placement: "bottom",
        copy: copy(
          "Inbox filters",
          "Switch between unread, read, tasks, or view everything. Counts show how many items are in each bucket."
        ),
      },
      {
        selector: '[data-tour="message-center-inbox"]',
        placement: "top",
        copy: copy(
          "Inbox list",
          "Tap a row to expand details, update status, add notes, or jump to the related lead when a link is available."
        ),
      },
    ],
    role,
    options
  );

export const FOLLOW_UP_INTEL_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Follow-Up Intelligence",
        "A manager board for overdue, upcoming, at-risk, and system follow-ups so you can prioritize outreach."
      ),
      {
        roles: LEADERSHIP,
        selector: '[data-tour="follow-up-summary"]',
        placement: "bottom",
        copy: copy(
          "Summary snapshot",
          "Quick counts across lanes—focus on overdue first, then upcoming and at-risk leads."
        ),
      },
      {
        roles: LEADERSHIP,
        selector: '[data-tour="follow-up-filters"]',
        placement: "bottom",
        copy: copy(
          "Territory filter",
          "Limit the board to one territory when you coach a specific team or market."
        ),
      },
      {
        roles: LEADERSHIP,
        selector: '[data-tour="follow-up-board"]',
        placement: "top",
        copy: copy(
          "Follow-up lanes",
          "Each column is a priority lane. Open cards to see lead details and take action."
        ),
      },
    ],
    role,
    options
  );

export const CONVERSATIONS_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Live SMS",
        "Two-way texting with leads. Pick a conversation, read the thread, and reply from one place."
      ),
      {
        selector: '[data-tour="conversations-new"]',
        placement: "left",
        copy: copy(
          "New conversation",
          "Start a new SMS thread when you need to reach a lead who is not already in your list."
        ),
      },
      {
        selector: '[data-tour="conversations-search"]',
        placement: "bottom",
        copy: copy(
          "Search conversations",
          "Find threads by lead name or phone number when your inbox grows."
        ),
      },
      {
        selector: '[data-tour="conversations-thread"]',
        placement: "left",
        copy: copy(
          "Message thread",
          "Select a conversation to read history and send replies. Lead details and notes are available from the thread tools."
        ),
      },
    ],
    role,
    options
  );

export const REP_POSITIONS_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Rep Positions",
        "See where reps are working in the field on a live map—useful for coverage and coaching."
      ),
      {
        roles: LEADERSHIP,
        selector: '[data-tour="rep-positions-filter"]',
        placement: "bottom",
        copy: copy(
          "Territory filter",
          "Focus the map on one territory to see which reps are active in that area."
        ),
      },
      {
        roles: LEADERSHIP,
        selector: '[data-tour="rep-positions-map"]',
        placement: "top",
        copy: copy(
          "Live map",
          "Pins show recent rep locations. Click a pin for name, territory, and last active time."
        ),
      },
    ],
    role,
    options
  );

export const CHANGELOG_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "What's New",
        "Product release notes and updates. Check here after upgrades to see what changed."
      ),
      {
        selector: '[data-tour="changelog-releases"]',
        placement: "top",
        copy: copy(
          "Release list",
          "Browse published releases with added, improved, and fixed items. Admins can draft and publish new entries."
        ),
      },
    ],
    role,
    options
  );

export const COMPANY_PROFILE_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Company Profile",
        "Your organization name, branding, and contact details used on proposals and customer touchpoints."
      ),
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="company-profile-logo"]',
        placement: "center",
        copy: copy(
          "Logo & branding",
          "Upload your company logo for proposals and the app header. Keep file size within the limit shown."
        ),
      },
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="company-profile-form"]',
        placement: "top",
        copy: copy(
          "Business details",
          "Update address, phone, email, and tax information, then save so reps quote with correct company data."
        ),
      },
    ],
    role,
    options
  );

export const ROLES_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Roles & Groups",
        "Understand what each role can access in the CRM before you assign users."
      ),
      {
        roles: SETTINGS_FULL,
        requiresRoleSettingsAccess: true,
        selector: '[data-tour="roles-overview"]',
        placement: "top",
        copy: copy(
          "Role definitions",
          "Each card summarizes permissions for Admin, Sales Director, Manager, and Sales Rep."
        ),
      },
    ],
    role,
    options
  );

export const USERS_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Users",
        "Invite teammates, assign roles and territories, and manage access from this directory."
      ),
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="users-toolbar"]',
        placement: "bottom",
        copy: copy(
          "User actions",
          "Search and filter the directory, then add users or open a row to edit role, territories, and status."
        ),
      },
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="users-table"]',
        placement: "top",
        copy: copy(
          "User list",
          "All CRM users appear here. Use edit or delete actions per row when you have permission."
        ),
      },
    ],
    role,
    options
  );

export const REPORTS_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Reports",
        "Build lead reports with filters, preview results in the app, or download to Excel."
      ),
      {
        roles: LEADERSHIP,
        selector: '[data-tour="reports-filters"]',
        placement: "bottom",
        copy: copy(
          "Report filters",
          "Choose status, territory, rep, dates, and property type before generating a view."
        ),
      },
      {
        roles: LEADERSHIP,
        selector: '[data-tour="reports-results"]',
        placement: "top",
        copy: copy(
          "Results table",
          "After you generate a report, leads appear here with export options for offline analysis."
        ),
      },
    ],
    role,
    options
  );

export const SUBSCRIPTION_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Subscriptions",
        "Review your plan, seat usage, and billing as the organization admin."
      ),
      {
        roles: ADMIN_ONLY,
        selector: '[data-tour="subscription-plan"]',
        placement: "bottom",
        copy: copy(
          "Plan & usage",
          "See current subscription tier, active seats, and limits. Manage billing from the actions on this page."
        ),
      },
    ],
    role,
    options
  );

export const INTEGRATIONS_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Integrations",
        "Connect email, maps, SMS, and other services that power notifications and automation."
      ),
      {
        roles: ADMIN_ONLY,
        selector: '[data-tour="integrations-email"]',
        placement: "top",
        copy: copy(
          "Email & SMTP",
          "Enable outbound email and configure your SMTP provider. Send a test email after saving."
        ),
      },
      {
        roles: ADMIN_ONLY,
        selector: '[data-tour="integrations-services"]',
        placement: "top",
        copy: copy(
          "Connected services",
          "Review status for maps, texting, and other integrations tied to your organization."
        ),
      },
    ],
    role,
    options
  );

export const ACTION_LOG_TOUR_STEPS = (role, options) =>
  tour(
    [
      welcome(
        "Action Log",
        "Audit trail of important changes—see who did what and when for accountability."
      ),
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="action-log-filters"]',
        placement: "bottom",
        copy: copy(
          "Log filters",
          "Narrow entries by user, action type, or date range to investigate specific events."
        ),
      },
      {
        roles: SETTINGS_FULL,
        selector: '[data-tour="action-log-table-header"]',
        placement: "bottom",
        copy: copy(
          "Activity logs",
          "The header shows how many entries match your filters. Admins can use Clean Up Logs to delete records older than a chosen number of days."
        ),
      },
    ],
    role,
    options
  );
